import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface ThemeTemplate {
  id: string;
  name: string;
  description: string | null;
  thumbnailUrl: string | null;
  category: 'FINTECH' | 'BANKING' | 'AGRO' | 'CRYPTO' | 'PREMIUM' | null;
  layoutType: 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';
  buttonStyle: 'SOLID' | 'OUTLINE' | 'GRADIENT' | 'GHOST' | 'GLASS';
  sidebarStyle: 'DARK' | 'LIGHT' | 'TRANSPARENT';
  buttonVariant: string | null;
  cardVariant: string | null;
  tableVariant: string | null;
  primaryColors: Record<string, string>;
  accentColors: Record<string, string>;
  surfaceColors: Record<string, string>;
  fontFamily: string | null;
  borderRadius: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateCategoryCount {
  category: string;
  count: number;
}

export interface ComponentPreview {
  type: 'button' | 'card' | 'table' | 'form' | 'modal';
  variant: string;
  previewHtml: string;
}

export interface ThemeTemplateWithPreview extends ThemeTemplate {
  previewComponents: ComponentPreview[];
}

export function useThemeTemplates(category?: string) {
  return useQuery({
    queryKey: ['theme-templates', category],
    queryFn: async () => {
      const params = category ? { category } : {};
      const response = await api.get<ThemeTemplate[]>('/theme-templates', { params });
      return response.data;
    },
  });
}

export function useTemplateCategories() {
  return useQuery({
    queryKey: ['theme-template-categories'],
    queryFn: async () => {
      const response = await api.get<TemplateCategoryCount[]>('/theme-templates/categories');
      return response.data;
    },
  });
}

export function useTemplatesByCategory(category: string) {
  return useQuery({
    queryKey: ['theme-templates', 'category', category],
    queryFn: async () => {
      const response = await api.get<ThemeTemplate[]>(`/theme-templates/category/${category}`);
      return response.data;
    },
    enabled: !!category,
  });
}

export function useSearchTemplates(query: string) {
  return useQuery({
    queryKey: ['theme-templates', 'search', query],
    queryFn: async () => {
      const response = await api.get<ThemeTemplate[]>('/theme-templates/search', {
        params: { q: query },
      });
      return response.data;
    },
    enabled: query.length >= 2,
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['theme-template', id],
    queryFn: async () => {
      const response = await api.get<ThemeTemplate>(`/theme-templates/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useTemplateWithPreview(id: string) {
  return useQuery({
    queryKey: ['theme-template', id, 'preview'],
    queryFn: async () => {
      const response = await api.get<ThemeTemplateWithPreview>(`/theme-templates/${id}/preview`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useApplyTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, templateId }: { tenantId: string; templateId: string }) => {
      const response = await api.post(`/theme-templates/${tenantId}/apply/${templateId}`);
      return response.data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['theme-templates'] });
    },
  });
}
